import React from "react";

import Table from "@cloudscape-design/components/table";
import Pagination from "@cloudscape-design/components/pagination";
import Header from "@cloudscape-design/components/header";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import Preferences from "@cloudscape-design/components/collection-preferences";
import Alert from "@cloudscape-design/components/alert";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Input from "@cloudscape-design/components/input";
import Toggle from "@cloudscape-design/components/toggle";
import Container from "@cloudscape-design/components/container";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import { useRecoilValue, useSetRecoilState } from "recoil";

import { configStore } from "../stores/config";
import { freespinsRulesStore } from "../stores/freespinsRules";
import VipFreespinsSettings from "./VipFreespinsSettings";

const SUPPORTED_CONFIG_KEYS = [
	"minDeposits30d",
	"minDeposits60d",
	"minDepositsAllTime",
	"minVipLevel",
	"minRegistrationAgeDays",
	"maxFreespinsAmount30d",
	"minDaysBetweenGrants",
];

const SUPPORT_LIMIT_CONFIG_KEYS = [
	"supportReasonGrantLimits",
];
const MAX_SUPPORT_LIMIT_ROWS = 3;

const CONFIG_FIELD_DEFINITIONS = [
	{
		key: "minDeposits30d",
		label: "Minimum deposits in last 30 days",
		description: "Player must have at least this deposit amount over the last 30 days.",
		type: "number",
	},
	{
		key: "minDeposits60d",
		label: "Minimum deposits in last 60 days",
		description: "Player must have at least this deposit amount over the last 60 days.",
		type: "number",
	},
	{
		key: "minDepositsAllTime",
		label: "Minimum deposits all time",
		description: "Player must have at least this total deposit amount.",
		type: "number",
	},
	{
		key: "minVipLevel",
		label: "Minimum VIP level",
		description: "Player's last granted VIP level must be at least this value.",
		type: "integer",
	},
	{
		key: "minRegistrationAgeDays",
		label: "Minimum registration age days",
		description: "Player account must be at least this many days old.",
		type: "integer",
	},
	{
		key: "maxFreespinsAmount30d",
		label: "Max successful VIP FS grants in last 30 days",
		description: "Maximum count of successful VIP FreeSpins grants the player may have received in the last 30 days.",
		type: "number",
	},
	{
		key: "minDaysBetweenGrants",
		label: "Minimum days between grants",
		description: "Require this many full days since the player's previous grant.",
		type: "integer",
	},
];

const CONFIG_FIELD_BY_KEY = CONFIG_FIELD_DEFINITIONS.reduce((map, definition) => {
	map[definition.key] = definition;
	return map;
}, {});

const CONFIG_FIELD_ROWS = [
	[
		"minDeposits30d",
		"minDeposits60d",
	],
	[
		"minDepositsAllTime",
		"minVipLevel",
	],
	[
		"minRegistrationAgeDays",
		"maxFreespinsAmount30d",
	],
	[
		"minDaysBetweenGrants",
	],
];

const emptyConfigFields = () =>
	CONFIG_FIELD_DEFINITIONS.reduce((fields, definition) => {
		fields[definition.key] = definition.type === "boolean" ? false : "";
		return fields;
	}, {});

const emptySupportLimit = () => ({
	maxGrants: "",
	windowDays: "",
});

const emptyForm = () => ({
	id: null,
	reasonCode: "",
	reasonName: "",
	active: true,
	configFields: emptyConfigFields(),
	supportReasonGrantLimits: [],
	extraConfig: {},
});

const formatDate = (value) => {
	if (!value) return "-";

	const date = new Date(value);
	if (!Number.isFinite(date.getTime())) return "-";

	return date.toLocaleDateString() + " " + date.toLocaleTimeString();
};

const normalizeConfig = (config) => {
	if (!config) return {};

	if (typeof config === "string") {
		try {
			const parsed = JSON.parse(config);
			return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
		} catch (error) {
			return {};
		}
	}

	return typeof config === "object" && !Array.isArray(config) ? config : {};
};

const splitConfig = (config) => {
	const configFields = emptyConfigFields();
	const supportReasonGrantLimits = [];
	const extraConfig = {};

	Object.keys(config).forEach((key) => {
		if (SUPPORTED_CONFIG_KEYS.includes(key)) {
			configFields[key] = config[key] === null || config[key] === undefined ? "" : String(config[key]);
			return;
		}

		if (SUPPORT_LIMIT_CONFIG_KEYS.includes(key)) {
			if (Array.isArray(config[key])) {
				config[key].slice(0, MAX_SUPPORT_LIMIT_ROWS).forEach((limit) => {
					if (!limit || typeof limit !== "object" || Array.isArray(limit)) return;

					supportReasonGrantLimits.push({
						maxGrants: limit.maxGrants === null || limit.maxGrants === undefined ? "" : String(limit.maxGrants),
						windowDays: limit.windowDays === null || limit.windowDays === undefined ? "" : String(limit.windowDays),
					});
				});
			}
			return;
		}

		extraConfig[key] = config[key];
	});

	return { configFields, supportReasonGrantLimits, extraConfig };
};

class VipFreespinsRules extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			modalVisible: false,
			limitsModalVisible: false,
			mode: "create",
			form: emptyForm(),
			fieldErrors: {},
			formError: "",
			saving: false,
			updatingActiveId: null,
		};
	}

	componentDidMount() {
		this.loadRules();
	}

	loadRules = (overrides = {}) => {
		const { freespinsRules, config, setFreespinsRules, Interface } = this.props;
		const page = overrides.page || freespinsRules.pagination.current;
		const limit = overrides.limit || Number(config.freespinsRules.perPage);
		const sortBy = overrides.sortBy || freespinsRules.sorting.sortingColumn.column;
		const sortDirection = overrides.sortDirection || freespinsRules.sorting.sortingColumn.order;

		setFreespinsRules({
			...freespinsRules,
			loaded: false,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		});

		Interface.loadFreespinsRules({
			page,
			limit,
			sortBy,
			sortDirection,
		})
			.then((response) => {
				const pagination = response.pagination || {};

				setFreespinsRules({
					...freespinsRules,
					loaded: true,
					data: response.data || [],
					pagination: {
						current: pagination.page || 1,
						total: pagination.totalPages || 0,
						count: pagination.total || 0,
					},
					error: {
						text: "",
						code: 0,
						timestamp: 0,
					},
				});
			})
			.catch((error) => {
				setFreespinsRules({
					...freespinsRules,
					loaded: true,
					data: [],
					error,
				});
			});
	};

	openCreateModal = () => {
		this.setState({
			modalVisible: true,
			mode: "create",
			form: emptyForm(),
			fieldErrors: {},
			formError: "",
		});
	};

	openLimitsModal = () => {
		this.setState({ limitsModalVisible: true });
	};

	closeLimitsModal = () => {
		this.setState({ limitsModalVisible: false });
	};

	openEditModal = (rule) => {
		const { configFields, supportReasonGrantLimits, extraConfig } = splitConfig(normalizeConfig(rule.config));

		this.setState({
			modalVisible: true,
			mode: "edit",
			form: {
				id: rule.id,
				reasonCode: rule.reasonCode || "",
				reasonName: rule.reasonName || "",
				active: rule.active === true || rule.active === 1 || rule.active === "1",
				configFields,
				supportReasonGrantLimits,
				extraConfig,
			},
			fieldErrors: {},
			formError: "",
		});
	};

	closeModal = () => {
		this.setState({
			modalVisible: false,
			mode: "create",
			form: emptyForm(),
			fieldErrors: {},
			formError: "",
			saving: false,
		});
	};

	updateFormField = (field, value) => {
		this.setState((state) => ({
			form: {
				...state.form,
				[field]: value,
			},
			fieldErrors: {
				...state.fieldErrors,
				[field]: "",
			},
			formError: "",
		}));
	};

	updateConfigField = (field, value) => {
		this.setState((state) => ({
			form: {
				...state.form,
				configFields: {
					...state.form.configFields,
					[field]: value,
				},
			},
			fieldErrors: {
				...state.fieldErrors,
				[field]: "",
			},
			formError: "",
		}));
	};

	updateSupportLimitField = (blockKey, index, field, value) => {
		this.setState((state) => {
			const nextLimits = state.form[blockKey].map((limit, limitIndex) => (
				limitIndex === index ? { ...limit, [field]: value } : limit
			));
			const errorKey = `${blockKey}.${index}.${field}`;

			return {
				form: {
					...state.form,
					[blockKey]: nextLimits,
				},
				fieldErrors: {
					...state.fieldErrors,
					[errorKey]: "",
					[blockKey]: "",
				},
				formError: "",
			};
		});
	};

	addSupportLimit = (blockKey) => {
		this.setState((state) => {
			if (state.form[blockKey].length >= MAX_SUPPORT_LIMIT_ROWS) {
				return {
					fieldErrors: {
						...state.fieldErrors,
						[blockKey]: `Maximum ${MAX_SUPPORT_LIMIT_ROWS} limits are allowed`,
					},
					formError: "",
				};
			}

			return {
				form: {
					...state.form,
					[blockKey]: [
						...state.form[blockKey],
						emptySupportLimit(),
					],
				},
				fieldErrors: {
					...state.fieldErrors,
					[blockKey]: "",
				},
				formError: "",
			};
		});
	};

	removeSupportLimit = (blockKey, index) => {
		this.setState((state) => ({
			form: {
				...state.form,
				[blockKey]: state.form[blockKey].filter((limit, limitIndex) => limitIndex !== index),
			},
			fieldErrors: {
				...state.fieldErrors,
				[blockKey]: "",
			},
			formError: "",
		}));
	};

	buildConfig = (form) => {
		const config = { ...form.extraConfig };

		CONFIG_FIELD_DEFINITIONS.forEach((definition) => {
			const value = form.configFields[definition.key];

			if (definition.type === "boolean") {
				if (value) config[definition.key] = true;
				return;
			}

			const normalizedValue = String(value).trim();
			if (normalizedValue !== "") {
				config[definition.key] = Number(normalizedValue);
			}
		});

		SUPPORT_LIMIT_CONFIG_KEYS.forEach((key) => {
			if (!form[key].length) return;

			config[key] = form[key].map((limit) => ({
				maxGrants: Number(String(limit.maxGrants).trim()),
				windowDays: Number(String(limit.windowDays).trim()),
			}));
		});

		return config;
	};

	validateForm = () => {
		const { form, mode } = this.state;
		const errors = {};

		if (!form.reasonCode.trim()) errors.reasonCode = "Reason code is required";
		if (!form.reasonName.trim()) errors.reasonName = "Reason name is required";

		CONFIG_FIELD_DEFINITIONS.forEach((definition) => {
			if (definition.type === "boolean") return;

			const value = form.configFields[definition.key];
			if (value === null || value === undefined) return;

			const normalizedValue = String(value).trim();
			if (normalizedValue === "") return;

			const numberValue = Number(normalizedValue);

			if (!Number.isFinite(numberValue)) {
				errors[definition.key] = "Enter a valid number";
				return;
			}

			if (numberValue < 0) {
				errors[definition.key] = "Value must be greater than or equal to 0";
				return;
			}

			if (definition.type === "integer" && !Number.isInteger(numberValue)) {
				errors[definition.key] = "Enter a whole number";
			}
		});

		SUPPORT_LIMIT_CONFIG_KEYS.forEach((key) => {
			const limits = form[key] || [];

			if (limits.length > MAX_SUPPORT_LIMIT_ROWS) {
				errors[key] = `Maximum ${MAX_SUPPORT_LIMIT_ROWS} limits are allowed`;
				return;
			}

			limits.forEach((limit, index) => {
				["maxGrants", "windowDays"].forEach((field) => {
					const value = String(limit[field] || "").trim();
					const errorKey = `${key}.${index}.${field}`;

					if (!value) {
						errors[errorKey] = "Required";
						return;
					}

					const numberValue = Number(value);
					if (!Number.isInteger(numberValue) || numberValue <= 0) {
						errors[errorKey] = "Enter a whole number greater than 0";
					}
				});
			});
		});

		if (Object.keys(errors).length) {
			return { valid: false, errors };
		}

		const payload = {
			reasonCode: form.reasonCode.trim(),
			reasonName: form.reasonName.trim(),
			active: form.active ? 1 : 0,
			config: this.buildConfig(form),
		};

		if (mode === "edit") payload.id = form.id;

		return { valid: true, payload, errors: {} };
	};

	submitForm = async () => {
		const validation = this.validateForm();

		if (!validation.valid) {
			this.setState({
				fieldErrors: validation.errors,
				formError: "Please fix the highlighted fields before saving.",
			});
			return;
		}

		this.setState({ saving: true, formError: "" });

		try {
			if (this.state.mode === "edit") {
				await this.props.Interface.updateFreespinsRule(validation.payload);
				this.props.Interface.addNoty("Success", "VIP FS rule updated successfully");
			} else {
				await this.props.Interface.createFreespinsRule(validation.payload);
				this.props.Interface.addNoty("Success", "VIP FS rule created successfully");
			}

			this.closeModal();
			this.loadRules();
		} catch (error) {
			this.setState({
				saving: false,
				formError: error.text || error.message || String(error),
			});
		}
	};

	updateActive = async (rule, checked) => {
		this.setState({ updatingActiveId: rule.id });

		try {
			await this.props.Interface.updateFreespinsRule({
				id: rule.id,
				active: checked ? 1 : 0,
			});
			this.props.Interface.addNoty("Success", "VIP FS rule status updated");
			this.loadRules();
		} catch (error) {
			this.props.Interface.addNoty("Error", error.text || error.message || String(error));
		} finally {
			this.setState({ updatingActiveId: null });
		}
	};

	renderConfigField = (definition) => {
		const { form, fieldErrors, saving } = this.state;
		const value = form.configFields[definition.key];
		const label = <span style={{ display: "block", minHeight: "1.4em" }}>{definition.label}</span>;
		const description = <span style={{ display: "block", minHeight: "3.2em" }}>{definition.description}</span>;

		if (definition.type === "boolean") {
			return (
				<FormField key={definition.key} label={label} description={description} stretch>
					<Toggle
						checked={Boolean(value)}
						onChange={({ detail }) => this.updateConfigField(definition.key, detail.checked)}
						disabled={saving}
					>
						{value ? "Required" : "Not required"}
					</Toggle>
				</FormField>
			);
		}

		return (
			<FormField key={definition.key} label={label} description={description} errorText={fieldErrors[definition.key]} stretch>
				<Input
					type="number"
					value={value}
					onChange={({ detail }) => this.updateConfigField(definition.key, detail.value)}
					disabled={saving}
					placeholder="Optional"
				/>
			</FormField>
		);
	};

	renderSupportLimitsBlock = (blockKey, title) => {
		const { form, fieldErrors, saving } = this.state;
		const limits = form[blockKey] || [];

		return (
			<Container header={<Header variant="h3">{title}</Header>}>
				<SpaceBetween direction="vertical" size="m">
					<Box variant="small">One support account may create up to the configured successful VIP FreeSpins grants within each window.</Box>
					{limits.length ? (
						<SpaceBetween direction="vertical" size="s">
							{limits.map((limit, index) => (
								<div
									key={`${blockKey}-${index}`}
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr auto",
										columnGap: "24px",
										alignItems: "end",
									}}
								>
									<FormField label="Max successful grants" errorText={fieldErrors[`${blockKey}.${index}.maxGrants`]} stretch>
										<Input
											type="number"
											inputMode="numeric"
											value={limit.maxGrants}
											onChange={({ detail }) => this.updateSupportLimitField(blockKey, index, "maxGrants", detail.value)}
											disabled={saving}
											placeholder="3"
										/>
									</FormField>
									<FormField label="Window, days" errorText={fieldErrors[`${blockKey}.${index}.windowDays`]} stretch>
										<Input
											type="number"
											inputMode="numeric"
											value={limit.windowDays}
											onChange={({ detail }) => this.updateSupportLimitField(blockKey, index, "windowDays", detail.value)}
											disabled={saving}
											placeholder="1"
										/>
									</FormField>
									<Button onClick={() => this.removeSupportLimit(blockKey, index)} disabled={saving}>
										Remove
									</Button>
								</div>
							))}
						</SpaceBetween>
					) : (
						<Box variant="small">No support limits configured.</Box>
					)}
					<SpaceBetween direction="horizontal" size="s">
						<Button onClick={() => this.addSupportLimit(blockKey)} disabled={saving || limits.length >= MAX_SUPPORT_LIMIT_ROWS}>
							Add limit
						</Button>
						{fieldErrors[blockKey] ? (
							<Box color="text-status-error">{fieldErrors[blockKey]}</Box>
						) : null}
					</SpaceBetween>
				</SpaceBetween>
			</Container>
		);
	};

	renderFormModal() {
		const { modalVisible, mode, form, fieldErrors, formError, saving } = this.state;
		const extraConfigKeys = Object.keys(form.extraConfig || {});

		return (
			<Modal
				visible={modalVisible}
				onDismiss={this.closeModal}
				header={mode === "edit" ? "Edit VIP FS Rule" : "Create VIP FS Rule"}
				size="large"
				footer={
					<Box float="right">
						<SpaceBetween direction="horizontal" size="xs">
							<Button variant="link" onClick={this.closeModal} disabled={saving}>
								Cancel
							</Button>
							<Button variant="primary" onClick={this.submitForm} loading={saving} disabled={saving}>
								{mode === "edit" ? "Save" : "Create"}
							</Button>
						</SpaceBetween>
					</Box>
				}
			>
				<SpaceBetween direction="vertical" size="m">
					{formError ? (
						<Alert type="error" header="Unable to save rule">
							{formError}
						</Alert>
					) : null}
					<Container header={<Header variant="h3">Basic</Header>}>
						<SpaceBetween direction="vertical" size="m">
							<ColumnLayout columns={2}>
								<FormField
									label="Reason Code"
									description="Identifier sent with the FreeSpins reason."
									errorText={fieldErrors.reasonCode}
									stretch
								>
									<Input
										value={form.reasonCode}
										onChange={({ detail }) => this.updateFormField("reasonCode", detail.value)}
										disabled={saving}
										placeholder="LOYALTY_REWARD"
									/>
								</FormField>
								<FormField
									label="Reason Name"
									description="Readable name shown to panel users."
									errorText={fieldErrors.reasonName}
									stretch
								>
									<Input
										value={form.reasonName}
										onChange={({ detail }) => this.updateFormField("reasonName", detail.value)}
										disabled={saving}
										placeholder="Loyalty Free Spins"
									/>
								</FormField>
							</ColumnLayout>
							<FormField stretch>
								<Toggle checked={form.active} onChange={({ detail }) => this.updateFormField("active", detail.checked)} disabled={saving}>
									{form.active ? "Active" : "Inactive"}
								</Toggle>
							</FormField>
						</SpaceBetween>
					</Container>
					<Container header={<Header variant="h3">Eligibility conditions</Header>}>
						<SpaceBetween direction="vertical" size="m">
							<Box variant="small">Leave optional fields empty to omit them from the saved config.</Box>
							<SpaceBetween direction="vertical" size="m">
								{CONFIG_FIELD_ROWS.map((row) => (
									<ColumnLayout key={row.join("-")} columns={2}>
										{row.map((key) => this.renderConfigField(CONFIG_FIELD_BY_KEY[key]))}
									</ColumnLayout>
								))}
							</SpaceBetween>
							{extraConfigKeys.length ? (
								<Alert type="info" header="Additional config will be preserved">
									This rule contains existing config keys not shown in the form: {extraConfigKeys.join(", ")}.
								</Alert>
							) : null}
						</SpaceBetween>
					</Container>
					{this.renderSupportLimitsBlock("supportReasonGrantLimits", "Support limits for this reason")}
				</SpaceBetween>
			</Modal>
		);
	}

	render() {
		const { freespinsRules, setFreespinsRules, config, setConfig } = this.props;
		const { updatingActiveId } = this.state;

		return (
			<React.Fragment>
				{this.renderFormModal()}
				<VipFreespinsSettings
					Interface={this.props.Interface}
					visible={this.state.limitsModalVisible}
					onDismiss={this.closeLimitsModal}
				/>

				{freespinsRules.loaded && (freespinsRules.error.text || "").length ? (
					<Box margin={{ bottom: "m" }}>
						<Alert
							type="error"
							header="Error loading data"
							dismissible
							onDismiss={() => setFreespinsRules({ ...freespinsRules, error: { text: "", code: 0, timestamp: 0 } })}
						>
							<Box variant="p">{freespinsRules.error.text}</Box>
							<hr />
							<Box variant="p">
								Request error code: {freespinsRules.error.code} | Request timestamp: {freespinsRules.error.timestamp}
							</Box>
						</Alert>
					</Box>
				) : null}

				<Table
					stickyHeader
					trackBy="id"
					loading={!freespinsRules.loaded}
					sortingColumn={{ sortingField: freespinsRules.sorting.sortingColumn.column }}
					sortingDescending={freespinsRules.sorting.isDescending}
					onSortingChange={(sorting) => {
						setFreespinsRules({
							...freespinsRules,
							sorting: {
								...freespinsRules.sorting,
								sortingColumn: {
									order: !sorting.detail.isDescending ? "ASC" : "DESC",
									column: sorting.detail.sortingColumn.sortingField,
								},
								isDescending: sorting.detail.isDescending,
							},
						});
						setTimeout(() => this.loadRules({
							sortBy: sorting.detail.sortingColumn.sortingField,
							sortDirection: !sorting.detail.isDescending ? "ASC" : "DESC",
						}), 250);
					}}
					columnDefinitions={[
						{
							id: "reasonCode",
							header: "Reason Code",
							sortingField: "reasonCode",
							cell: (item) => (
								<Button variant="link" onClick={() => this.openEditModal(item)}>
									{item.reasonCode}
								</Button>
							),
						},
						{ id: "reasonName", header: "Reason Name", sortingField: "reasonName", cell: (item) => item.reasonName || "-" },
						{ id: "updatedAt", header: "Updated At", sortingField: "updatedAt", cell: (item) => formatDate(item.updatedAt) },
						{
							id: "active",
							header: "Active",
							sortingField: "active",
							width: 120,
							cell: (item) => {
								const active = item.active === true || item.active === 1 || item.active === "1";
								return (
									<Toggle checked={active} onChange={({ detail }) => this.updateActive(item, detail.checked)} disabled={updatingActiveId === item.id}>
										{active ? "On" : "Off"}
									</Toggle>
								);
							},
						},
					]}
					items={freespinsRules.data}
					pagination={
						<Pagination
							disabled={!freespinsRules.loaded}
							currentPageIndex={freespinsRules.pagination.current}
							pagesCount={freespinsRules.pagination.total}
							onChange={(page) => {
								setFreespinsRules({
									...freespinsRules,
									pagination: { ...freespinsRules.pagination, current: page.detail.currentPageIndex },
								});
								setTimeout(() => this.loadRules({ page: page.detail.currentPageIndex }), 250);
							}}
						/>
					}
					preferences={
						<Preferences
							cancelLabel="Cancel"
							confirmLabel="Confirm"
							pageSizePreference={{ title: "VIP FS rules per page", options: config.freespinsRules.preferences.perPage }}
							preferences={{ pageSize: Number(config.freespinsRules.perPage) }}
							title="Preferences"
							onConfirm={(preferences) => {
								localStorage.setItem("freespinsRules.perPage", preferences.detail.pageSize);
								setConfig({ ...config, freespinsRules: { ...config.freespinsRules, perPage: preferences.detail.pageSize } });
								setFreespinsRules({ ...freespinsRules, pagination: { ...freespinsRules.pagination, current: 1 } });
								setTimeout(() => this.loadRules({ page: 1, limit: Number(preferences.detail.pageSize) }), 250);
							}}
						/>
					}
					header={
						<Header
							variant="h2"
							description="Manage VIP FreeSpins reason identifiers and JSON-controlled eligibility conditions"
							counter={`(${freespinsRules.pagination.count})`}
							actions={
								<SpaceBetween direction="horizontal" size="xs">
									<Button disabled={!freespinsRules.loaded} onClick={this.openLimitsModal}>
										Limits
									</Button>
									<Button iconName="add-plus" disabled={!freespinsRules.loaded} variant="primary" onClick={this.openCreateModal}>
										Add VIP FS Rule
									</Button>
								</SpaceBetween>
							}
						>
							VIP FS Rules
						</Header>
					}
					empty={
						<Box textAlign="center" color="inherit">
							<b>No VIP FS rules found</b>
							<Box padding={{ bottom: "s" }} variant="p" color="inherit">
								Create a rule to manage a VIP FreeSpins reason.
							</Box>
							<Button onClick={this.openCreateModal}>Add VIP FS Rule</Button>
						</Box>
					}
				/>
			</React.Fragment>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const freespinsRules = useRecoilValue(freespinsRulesStore);
		const setFreespinsRules = useSetRecoilState(freespinsRulesStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);

		return <Component {...props} freespinsRules={freespinsRules} setFreespinsRules={setFreespinsRules} config={config} setConfig={setConfig} />;
	};
}

export default withRecoil(VipFreespinsRules);
